import { PROJECT_TYPES, requiresClient } from "@fieldpath/shared";
import type { ProjectType, ProjectView, ReferenceOption } from "@fieldpath/shared";
import { useEffect, useState } from "react";

import { api, ApiError, type Fetcher } from "../api/client";
import { useRefreshOnReturn } from "../refreshOnReturn";
import { isMyProject, nextSort, sortProjects, type ProjectSort, type SortColumn } from "../projectFilter";
import { HelpButton } from "./HelpButton";
import { SearchSelect } from "./SearchSelect";
import { KindTag } from "./KindTag";

interface Props {
  fetcher: Fetcher;
  onOpen: (project: ProjectView) => void;
}

/**
 * A project's state, for the filter. Closed is COMPLETED ("we did it");
 * cancelled is "we are not doing this". A row is one or the other, never both,
 * by check constraint, so the order below is only for clarity.
 */
const STATUSES = ["Open", "Closed", "Cancelled"] as const;
type ProjectStatus = (typeof STATUSES)[number];

function statusOf(p: ProjectView): ProjectStatus {
  if (p.cancelledAt) return "Cancelled";
  if (p.completedAt) return "Closed";
  return "Open";
}

/** The headings, in order, and the text each sorts by: what the cell shows (F178). */
const COLUMNS: readonly { column: SortColumn; label: string }[] = [
  { column: "name", label: "Name" },
  { column: "status", label: "Status" },
  { column: "type", label: "Type" },
  { column: "client", label: "Client" },
  { column: "pm", label: "PM" },
  { column: "tm", label: "TM" },
  { column: "ajera", label: "Ajera project" },
];

function cellText(p: ProjectView, column: SortColumn): string | null {
  switch (column) {
    case "name":
      return p.name;
    case "status":
      return statusOf(p);
    case "type":
      return p.projectType;
    case "client":
      return p.clientName;
    case "pm":
      return p.pmKey ? (p.pmName ?? p.pmKey) : null;
    case "tm":
      return p.tmKey ? (p.tmName ?? p.tmKey) : null;
    case "ajera":
      return p.deltekProjectName;
  }
}

export function ProjectList({ fetcher, onOpen }: Props) {
  const [projects, setProjects] = useState<ProjectView[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  // Open only, by default — Jeremy, 11 Oct 2026. Closed and cancelled work is
  // still one click away, but it is not what somebody opening this list came
  // to find, and after a beta reset it was most of the list. Not remembered
  // between visits: the default is the answer to "what is live", every time.
  const [shown, setShown] = useState<ReadonlySet<ProjectStatus>>(new Set(["Open"]));
  // Filters as it is typed, across the three names somebody might remember a
  // project by (punch list #1, 12 Oct 2026). Anywhere in the name, as
  // SearchSelect matches, because nobody remembers which word came first.
  const [q, setQ] = useState("");
  // MY PROJECTS (punch list F133, 30 Oct 2026): the ones where you are the PM
  // or the TM, matched on the Ajera KEY, never the name. A display filter
  // like the others; the list itself is everybody's.
  const [meKey, setMeKey] = useState<string | null>(null);
  const [mineOnly, setMineOnly] = useState(false);
  // Null until a heading is clicked: the server's order, newest first (F178).
  const [sort, setSort] = useState<ProjectSort | null>(null);

  useEffect(() => {
    void api
      .me(fetcher)
      .then((r) => setMeKey(r.employee?.ajeraKey ?? null))
      // Not knowing who you are hides the switch; the list still works.
      .catch(() => setMeKey(null));
  }, [fetcher]);

  const toggle = (s: ProjectStatus) =>
    setShown((prev) => {
      const next = new Set(prev);
      if (next.has(s)) next.delete(s);
      else next.add(s);
      return next;
    });
  const countOf = (s: ProjectStatus) => (projects ?? []).filter((p) => statusOf(p) === s).length;
  const needle = q.trim().toLowerCase();
  const matches = (p: ProjectView) =>
    !needle ||
    [p.name, p.clientName, p.deltekProjectName]
      .filter(Boolean)
      .join(" ")
      .toLowerCase()
      .includes(needle);
  const isMine = (p: ProjectView) => isMyProject(p, meKey);
  const visible = sortProjects(
    (projects ?? []).filter((p) => shown.has(statusOf(p)) && matches(p) && (!mineOnly || isMine(p))),
    sort,
    cellText,
  );

  // Read again quietly on coming back to the tab (punch list F172).
  const back = useRefreshOnReturn();
  const reload = () =>
    api
      .listProjects(fetcher)
      .then(setProjects)
      .catch((e: Error) => setError(e.message));

  useEffect(() => {
    void reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetcher, back]);

  return (
    <>
      {/* ACTIVE and TOTAL, which the list already knows. The handoff also
          asked for OPEN task counts and NEXT CLIENT DATE columns; `ProjectView`
          carries neither, and adding them is an API change this refresh is not
          allowed to make. Left out rather than guessed — see CLAUDE.md. */}
      <div className="count-line">
        {(projects ?? []).filter((p) => !p.completedAt && !p.cancelledAt).length} ACTIVE
        {" · "}
        {(projects ?? []).length} TOTAL
      </div>
      <div className="row spread" style={{ marginBottom: 20 }}>
        <h1 className="page-h1" style={{ margin: 0 }}>
          Projects
          <HelpButton screens="projects" />
        </h1>
        {/* Outlined with its PRJ tag. A project is the biggest thing anyone
            creates here and the rarest; the primary fill is kept for the
            action a screen is actually FOR. */}
        <button className="create" onClick={() => setCreating((c) => !c)}>
          {creating ? (
            "Cancel"
          ) : (
            <>
              <KindTag kind="project" size="row" />
              New project
            </>
          )}
        </button>
      </div>

      {error ? <div className="error">{error}</div> : null}

      {creating ? (
        <NewProject
          fetcher={fetcher}
          // Straight into the new project, ready for deliverables and tasks
          // (punch list F124, 30 Oct 2026). It came back to this list until
          // then, and the next click was always the row just made.
          onCreated={(project) => onOpen(project)}
        />
      ) : null}

      {/* The same toggles as the Overview, so the two screens filter the
          same way. Each says how many it holds, so "nothing is showing" can
          never be mistaken for "there is nothing". */}
      {projects && projects.length > 0 ? (
        <div className="filters">
          <input
            type="search"
            value={q}
            placeholder="Search project, client or Ajera project…"
            aria-label="Search projects"
            onChange={(e) => setQ(e.target.value)}
            className="filter-search"
          />
          <div className="filter-row">
            {meKey !== null ? (
              <button
                className={`chip${mineOnly ? " on" : ""}`}
                aria-pressed={mineOnly}
                title="Projects where you are the PM or the TM"
                onClick={() => setMineOnly((m) => !m)}
              >
                My projects ({(projects ?? []).filter(isMine).length})
              </button>
            ) : null}
            {STATUSES.map((s) => (
              <button
                key={s}
                className={`chip${shown.has(s) ? " on" : ""}`}
                aria-pressed={shown.has(s)}
                onClick={() => toggle(s)}
              >
                {s} ({countOf(s)})
              </button>
            ))}
          </div>
        </div>
      ) : null}

      <div className="card">
        {projects === null ? (
          <p className="muted">Loading…</p>
        ) : projects.length === 0 ? (
          <p className="muted">No projects yet.</p>
        ) : visible.length === 0 ? (
          <p className="muted">No projects match these filters.</p>
        ) : (
          <table>
            <thead>
              <tr>
                {/* Click to sort, click again to reverse (punch list F178). */}
                {COLUMNS.map(({ column, label }) => {
                  const on = sort?.column === column;
                  return (
                    <th key={column} aria-sort={on ? (sort.descending ? "descending" : "ascending") : "none"}>
                      <button
                        type="button"
                        className={on ? "th-sort on" : "th-sort"}
                        onClick={() => setSort((s) => nextSort(s, column))}
                      >
                        {label}
                        {on ? <span aria-hidden="true">{sort.descending ? " ▼" : " ▲"}</span> : null}
                      </button>
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {visible.map((p) => (
                // Punch list #77: the ROW says closed or cancelled, not only the
                // chip -- done washes lime, cancelled greys and strikes the name.
                <tr
                  key={p.id}
                  className={
                    statusOf(p) === "Cancelled" ? "proj-row-cancelled" : statusOf(p) === "Closed" ? "proj-row-closed" : undefined
                  }
                >
                  <td>
                    {/* A flex row with a gap, because JSX drops the line break
                        between elements: the tag sat flush against the name
                        ("PRJOnly Open one") until 11 Oct. Found by rendering. */}
                    <span className="proj-list-name">
                      <KindTag kind="project" size="row" />
                      <button className="linkish proj-name" onClick={() => onOpen(p)}>
                        {p.name}
                      </button>
                    </span>
                  </td>
                  <td>
                    {/* Its own column since punch list #1 (12 Oct). A closed
                        project shown beside live ones still cannot be read as
                        live: the same chips the project page uses. */}
                    {statusOf(p) === "Cancelled" ? (
                      <span className="chip cancelled">Cancelled</span>
                    ) : statusOf(p) === "Closed" ? (
                      <span className="chip completed">Closed</span>
                    ) : (
                      <span className="chip">Open</span>
                    )}
                  </td>
                  <td>
                    <span className="chip">{p.projectType}</span>
                  </td>
                  <td>
                    {p.clientName ? (
                      <span className="client-name">{p.clientName}</span>
                    ) : (
                      <span className="muted">—</span>
                    )}
                  </td>
                  {/* Display only (E9.3), as on the project page. */}
                  <td className={p.pmKey ? undefined : "muted"}>{p.pmKey ? (p.pmName ?? p.pmKey) : "—"}</td>
                  <td className={p.tmKey ? undefined : "muted"}>{p.tmKey ? (p.tmName ?? p.tmKey) : "—"}</td>
                  <td className="muted">{p.deltekProjectName ?? "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </>
  );
}

function NewProject({
  fetcher,
  onCreated,
}: {
  fetcher: Fetcher;
  onCreated: (project: ProjectView) => void;
}) {
  const [name, setName] = useState("");
  const [projectType, setProjectType] = useState<ProjectType>("Client");
  const [clientKey, setClientKey] = useState("");
  const [deltekProjectKey, setDeltekProjectKey] = useState("");
  const [clients, setClients] = useState<ReferenceOption[]>([]);
  const [deltekProjects, setDeltekProjects] = useState<ReferenceOption[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const needsClient = requiresClient(projectType);

  useEffect(() => {
    void api.clients(fetcher).then(setClients).catch(() => undefined);
  }, [fetcher]);

  // Ajera projects are fetched per client: the database refuses one whose
  // client disagrees, so offering the rest would only produce a rejection.
  useEffect(() => {
    setDeltekProjectKey("");
    if (!clientKey) {
      setDeltekProjects([]);
      return;
    }
    void api
      .deltekProjects(fetcher, clientKey)
      .then(setDeltekProjects)
      .catch(() => setDeltekProjects([]));
  }, [fetcher, clientKey]);

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    setError(null);
    setSaving(true);
    try {
      const project = await api.createProject(fetcher, {
        name,
        projectType,
        clientKey: needsClient && clientKey ? (clientKey as never) : null,
        deltekProjectKey: deltekProjectKey ? (deltekProjectKey as never) : null,
      });
      onCreated(project);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <form className="card" onSubmit={submit}>
      <h2>New project</h2>
      {error ? <div className="error">{error}</div> : null}

      <div className="field">
        <label htmlFor="p-name">Name</label>
        <input id="p-name" value={name} onChange={(e) => setName(e.target.value)} required />
      </div>

      <div className="field">
        <label htmlFor="p-type">Type</label>
        <select
          id="p-type"
          value={projectType}
          onChange={(e) => setProjectType(e.target.value as ProjectType)}
        >
          {PROJECT_TYPES.map((t) => (
            <option key={t} value={t}>
              {t}
            </option>
          ))}
        </select>
      </div>

      {needsClient ? (
        <>
          <div className="field">
            <label htmlFor="p-client">Client (required for {projectType} work)</label>
            <SearchSelect
              id="p-client"
              options={clients}
              value={clientKey}
              onChange={setClientKey}
              emptyLabel={`Start typing a client — ${clients.length} to choose from`}
            />
          </div>

          <div className="field">
            <label htmlFor="p-ajera">Ajera project (optional)</label>
            <SearchSelect
              id="p-ajera"
              options={deltekProjects}
              value={deltekProjectKey}
              onChange={setDeltekProjectKey}
              disabled={!clientKey}
              emptyLabel={
                clientKey
                  ? `Start typing — ${deltekProjects.length} for this client`
                  : "Choose a client first"
              }
            />
          </div>
        </>
      ) : (
        <p className="muted">
          {projectType} projects have no client, so they cannot be linked to an Ajera project.
        </p>
      )}

      <button className="primary" type="submit" disabled={saving}>
        {saving ? "Creating…" : "Create project"}
      </button>
    </form>
  );
}
